import { fieldReferencePattern, normalizeFieldLabel } from './field-chips';

// The elements PlainText in pkg/service/api/emailtemplate starts a new line at.
const lineBreakingElements = new Set([
  'BR',
  'P',
  'DIV',
  'BLOCKQUOTE',
  'PRE',
  'HR',
  'UL',
  'OL',
  'LI',
  'TABLE',
  'TR',
  'TD',
  'TH',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6'
]);
// No end tag, so the server's tokenizer meets these once.
const voidElements = new Set(['BR', 'HR']);
const nonTextElements = new Set(['SCRIPT', 'STYLE']);

/** The text a reader sees in bodyHtml, with a line break at each block, as the server reads it. */
export const plainText = (bodyHtml: string): string => {
  const body = new DOMParser().parseFromString(bodyHtml, 'text/html').body;
  let text = '';
  const read = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? '';
      return;
    }
    if (!(node instanceof Element) || nonTextElements.has(node.tagName)) return;
    const breaksLine = lineBreakingElements.has(node.tagName);
    if (breaksLine) text += '\n';
    node.childNodes.forEach(read);
    if (breaksLine && !voidElements.has(node.tagName)) text += '\n';
  };
  body.childNodes.forEach(read);
  return text.trim();
};

/**
 * Each of the draft's placeholder labels still written as [[label]] in the subject or body, once,
 * subject first. The server's BlankFields refuses the send on the same rule; this lets the
 * composer say so first. A line break inside a blank still counts, so an agent pressing Enter in
 * one can't send a stray "[[renewal".
 */
export const blankTemplateFields = (
  subject: string,
  bodyHtml: string,
  placeholderLabels: string[]
): string[] => {
  const draftLabels = new Set(placeholderLabels.map(normalizeFieldLabel));
  const blank: string[] = [];
  for (const text of [subject, plainText(bodyHtml)]) {
    const textOnOneLine = text.replace(/[\r\n]+/g, ' ');
    for (const match of textOnOneLine.matchAll(fieldReferencePattern)) {
      const label = normalizeFieldLabel(match[1]);
      if (draftLabels.has(label) && !blank.includes(label)) blank.push(label);
    }
  }
  return blank;
};

/** "a", "a and b", "a, b and c". */
export const joinWithAnd = (phrases: string[]) =>
  phrases.length <= 1
    ? (phrases[0] ?? '')
    : `${phrases.slice(0, -1).join(', ')} and ${phrases[phrases.length - 1]}`;

/** Why Send is off while fields are blank, worded as the server's refusal is. */
export const blankFieldReason = (blankLabels: string[]): string | undefined =>
  blankLabels.length > 0
    ? `Fill in or remove ${joinWithAnd(blankLabels.map((label) => `[[${label}]]`))} before sending.`
    : undefined;
